import { useMutation, useQuery } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { gql } from "generated-graphql";
import {
  FacilityPickerFragment,
  FacilityStatus,
  NotReportingReason,
} from "generated-graphql/graphql";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import { REPORTS_QUERY } from ".";
import { GET_REPORT } from "../Report/useReport";
import {
  getValidationSchema as getValidationSchemaFromFields,
  NotReportingReasonFields,
  ReasonFormInput,
} from "./NotReportingReasonFields";

/**
 * This component was originally created for the fulfillment and other report
 * tables.  This interface makes it a bit more generic in that we don't have a
 * specific type that is required. The data just needs to match the following
 * interface
 */
export interface NotReportingItem {
  reason?: NotReportingReason;
  facilityId: string;
  reportingYear: number;
  tenantId: string;
  tierIIReportId?: string | null;
  // Stored reason/note on the report. When present these are used to pre-fill the
  // dialog (preferred over the facility-derived fallback below).
  notReportingReason?: NotReportingReason | null;
  notReportingReasonNote?: string | null;
  facilityDateClosed?: string | null;
  facilityDateInactive?: string | null;
  colocationReportingFacilityId?: string | null;
  // Optional (not required) on purpose: the GraphQL source types this as
  // `FacilityStatus | null | undefined`, so requiring it would force every caller
  // to coalesce `?? null` — re-introducing the per-call remap the rename removed.
  facilityStatus?: FacilityStatus | null;
  isNotReporting?: boolean | null;
  isNotReportingThroughEncamp?: boolean | null;
}

interface NotReportingModalProps<T extends NotReportingItem> {
  reports: T[];
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void; // Callback for successful submission (e.g., to clear selections)
}

type FormInput = ReasonFormInput;

const COLOCATION_FACILITY = gql(`
  query CoLocatedFacility($id: ID!) {
    facility(id: $id) {
      ...FacilityPicker
    }
  }
`);

export const BULK_UPDATE_NOT_REPORTING_REASON_MUTATION = gql(`
  mutation BulkUpdateNotReportingReason($input: NotReportingReasonInput!) {
    bulkUpdateNotReportingReason(input: $input) {
      succeeded
    }
  }
`);

// Pre-fills the EXEMPT note from the most recent prior-year EXEMPT report. The reason
// never carries over (the user must re-select it); only the note is offered.
const PRIOR_EXEMPT_NOTE = gql(`
  query PriorExemptNote($facilityId: ID!, $beforeReportingYear: Int!) {
    priorExemptNote(facilityId: $facilityId, beforeReportingYear: $beforeReportingYear)
  }
`);

export function NotReportingModal<T extends NotReportingItem>(props: NotReportingModalProps<T>) {
  const { open, onClose, reports, onSuccess } = props;
  const prevRefOpen = useRef(open);
  const [coLocatedFacility, setCoLocatedFacility] = useState<FacilityPickerFragment | null>(null);

  const singleMode = reports.length === 1;

  // All selected reports should have the same reporting year for bulk operations
  // Mixed reporting years are now prevented at the UI level by disabling bulk actions
  const reportingYear = useMemo(() => {
    // All selected reports share a reporting year (bulk actions disabled across years).
    return reports[0]?.reportingYear ?? currentTierIIReportingYear;
  }, [reports]);

  const { loading } = useQuery(COLOCATION_FACILITY, {
    variables: { id: reports[0].colocationReportingFacilityId ?? "" },
    skip: !singleMode || !reports[0].colocationReportingFacilityId,
    onCompleted(data) {
      if (data.facility) {
        setCoLocatedFacility(data.facility);
      }
    },
  });

  // The most recent prior-year EXEMPT note, pre-filled (in the reason change handler below)
  // when EXEMPT is selected. The reason itself never carries over year-to-year; only the note.
  const { data: priorExemptData } = useQuery(PRIOR_EXEMPT_NOTE, {
    variables: {
      facilityId: reports[0]?.facilityId ?? "",
      beforeReportingYear: reportingYear,
    },
    skip: !singleMode || !reports[0]?.facilityId,
  });
  const priorExemptNote = priorExemptData?.priorExemptNote ?? undefined;

  const defaultValues = useMemo<FormInput>(() => {
    let defaultOption: NotReportingReason | undefined = undefined;
    if (singleMode) {
      const reportData = reports[0];
      if (reportData?.notReportingReason) {
        // Prefer the explicit stored reason over the facility-derived fallback.
        defaultOption = reportData.notReportingReason;
      } else if (reportData?.reason) {
        defaultOption = reportData.reason;
      } else {
        if (reportData?.facilityStatus === FacilityStatus.Closed) {
          defaultOption = NotReportingReason.Closed;
        } else if (reportData?.facilityStatus === FacilityStatus.Inactive) {
          defaultOption = NotReportingReason.Inactive;
        } else if (reportData?.colocationReportingFacilityId) {
          defaultOption = NotReportingReason.Colocated;
        } else if (reportData?.isNotReporting) {
          defaultOption = NotReportingReason.UnderThreshold;
        } else if (reportData?.isNotReportingThroughEncamp) {
          defaultOption = NotReportingReason.NotWithEncamp;
        } else {
          defaultOption = NotReportingReason.Closed;
        }
      }
    }
    return {
      selectedOption: defaultOption,
      dateClosed:
        singleMode && reports[0].facilityDateClosed ? reports[0].facilityDateClosed : undefined,
      dateInactive:
        singleMode && reports[0].facilityDateInactive ? reports[0].facilityDateInactive : undefined,
      coLocatedId:
        singleMode && reports[0] ? (reports[0].colocationReportingFacilityId ?? undefined) : "",
      note: singleMode ? (reports[0]?.notReportingReasonNote ?? undefined) : undefined,
    };
  }, [reports, singleMode]);

  const validationSchema = useMemo(
    () => getValidationSchemaFromFields(reportingYear),
    [reportingYear]
  );

  const { handleSubmit, control, reset, setValue, getValues } = useForm<FormInput>({
    resolver: yupResolver(validationSchema) as any,
    defaultValues,
    mode: "onChange", // Validate on every change
  });

  const [bulkUpdateNotReportingReason, { loading: saving }] = useMutation(
    BULK_UPDATE_NOT_REPORTING_REASON_MUTATION,
    {
      awaitRefetchQueries: true,
      onCompleted: () => {
        alerts.success(`Saved report${props.reports.length > 1 ? "s" : ""} successfully`);
      },
      // No onError: it swallows the rejection and hides the server's specific
      // message (e.g. the exempt-gate explanation). onSubmit catches instead.
      refetchQueries: [REPORTS_QUERY, GET_REPORT],
    }
  );
  const alerts = useAlerts();

  useEffect(() => {
    if (prevRefOpen.current === false && open === true) {
      reset(defaultValues);
    }
    prevRefOpen.current = open;
  }, [open, reset, defaultValues]);

  const onSubmit = useCallback(
    async (input: FormInput) => {
      if (!input.selectedOption) {
        return;
      }
      try {
        const result = await bulkUpdateNotReportingReason({
          variables: {
            input: {
              facilityIds: props.reports.map((report) => report.facilityId),
              reason: input.selectedOption,
              reportingYear: reportingYear,
              dateClosed: input.dateClosed,
              dateInactive: input.dateInactive,
              coLocatedId: input.coLocatedId,
              note: input.selectedOption === NotReportingReason.Exempt ? input.note : undefined,
            },
          },
        });
        if (!result.data?.bulkUpdateNotReportingReason?.succeeded) {
          alerts.error("Error saving reports.");
          return;
        }
        // Clear form state after successful submission
        reset();
        // Call success callback (e.g., to clear grid selections)
        onSuccess?.();
        onClose();
      } catch (err) {
        // Surface the server's specific message (e.g. the exempt gate naming the
        // facilities that still have reporting chemicals) rather than a generic one.
        alerts.error(
          err instanceof Error ? err.message : "An error occurred while saving the reports."
        );
      }
    },
    // oxlint-disable-next-line exhaustive-deps -- Grandfathered in error. Future hooks should be exhaustive
    [bulkUpdateNotReportingReason, props.reports, alerts, onClose, onSuccess, reset]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      title="Set Facility as Not Reporting"
      primaryActionButtonProps={{
        onClick: handleSubmit(onSubmit),
        loading: saving,
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <NotReportingReasonFields
          control={control}
          reportingYear={reportingYear}
          tenantId={reports[0].tenantId}
          disabledFacilityIds={props.reports.map((report) => report.facilityId)}
          coLocatedFacility={coLocatedFacility}
          setCoLocatedFacility={setCoLocatedFacility}
          priorExemptNote={priorExemptNote}
          setValue={setValue}
          getValues={getValues}
          coLocatedLoading={loading}
          hasDefaultCoLocatedId={!!defaultValues.coLocatedId}
        />
      </form>
    </Dialog>
  );
}
